import { readSessionItem, writeSessionItem } from 'utils/session-storage';

export type UtmParams = Record<string, string>;

/**
 * UTM-метки сохраняются при первом заходе на сайт и уходят на бэкенд
 * вместе с первым запросом кода, поэтому они должны пережить переходы
 * по страницам и перезагрузку: sessionStorage, как и шаг регистрации.
 */
const STORAGE_KEY = 'self-registration:utm';
/** Запасной источник меток, если их нет в ссылке: JSON в cookie на домене .pro-data.tech */
const COOKIE_NAME = 'pd_utm';
const UTM_PREFIX = 'utm_';

const pickUtm = (entries: Iterable<[string, unknown]>): UtmParams => {
  const result: UtmParams = {};
  Array.from(entries).forEach(([key, value]) => {
    const name = key.toLowerCase();
    if (name.startsWith(UTM_PREFIX) && typeof value === 'string' && value) {
      result[name] = value;
    }
  });
  return result;
};

const readFromUrl = (): UtmParams => pickUtm(new URLSearchParams(window.location.search).entries());

const readFromCookie = (): UtmParams => {
  try {
    const raw = document.cookie
      .split('; ')
      .find((item) => item.startsWith(`${COOKIE_NAME}=`))
      ?.slice(COOKIE_NAME.length + 1);
    if (!raw) return {};

    const parsed = JSON.parse(decodeURIComponent(raw));
    return parsed && typeof parsed === 'object' ? pickUtm(Object.entries(parsed)) : {};
  } catch {
    return {};
  }
};

const isEmpty = (params: UtmParams) => Object.keys(params).length === 0;

/**
 * Возвращает метки первого захода: сохранённые, иначе из ссылки, иначе из cookie.
 * Если меток нет нигде, возвращает undefined, и поле `utm` в запрос не добавляется.
 */
export const getUtm = (): UtmParams | undefined => {
  const saved = readSessionItem<UtmParams>(STORAGE_KEY, {});
  if (!isEmpty(saved)) return saved;

  const found = isEmpty(readFromUrl()) ? readFromCookie() : readFromUrl();
  if (isEmpty(found)) return undefined;

  writeSessionItem(STORAGE_KEY, found);
  return found;
};
